import React, { useCallback, useState, useEffect, useRef } from "react";
import { useDropzone } from "react-dropzone";
import { Toast } from "../../../shared/Toast";
import Loader from "../../../shared/Loader";
import API from "../../../services/api";
import { EmitEvent } from "../../../services/socket";
import RotatableImage from "../utils/RotatableImage";
const UploadModal = ({ isOpen, onClose, creativeId, creativeType, onUploadSuccess, onUploadError }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [fileType, setFileType] = useState(null);
  const dropzoneRef = useRef(null);

  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreviewUrl, setThumbnailPreviewUrl] = useState("");
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState("");

  useEffect(() => {
    if (!selectedFile) {
      setPreviewUrl("");
      setFileType(null);
      return;
    }
    const url = URL.createObjectURL(selectedFile);
    setPreviewUrl(url);
    setFileType(selectedFile.type.startsWith("image/") ? "image" : "video");
    return () => URL.revokeObjectURL(url);
  }, [selectedFile]);

  useEffect(() => {
    if (!thumbnailFile) {
      setThumbnailPreviewUrl("");
      return;
    }
    const url = URL.createObjectURL(thumbnailFile);
    setThumbnailPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [thumbnailFile]);

  useEffect(() => {
    if (!videoFile) {
      setVideoPreviewUrl("");
      return;
    }
    const url = URL.createObjectURL(videoFile);
    setVideoPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [videoFile]);

  useEffect(() => {
    if (isOpen && dropzoneRef.current) dropzoneRef.current.focus();
  }, [isOpen]);

  const onDrop = useCallback((files) => {
    if (!files.length) return Toast("error", "No valid file selected");
    setSelectedFile(files[0]);
  }, []);

  const onDropThumbnail = useCallback((files) => {
    if (!files.length) return Toast("error", "No valid file selected");
    setThumbnailFile(files[0]);
  }, []);

  const onDropVideo = useCallback((files) => {
    if (!files.length) return Toast("error", "No valid file selected");
    setVideoFile(files[0]);
  }, []);

  const handlePaste = useCallback((e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.kind !== "file") continue;
      const file = item.getAsFile();
      if (!file) continue;
      if (!file.type.startsWith("image/") && !file.type.startsWith("video/")) return;

      const newFile = new File([file], `clipboard-${Date.now()}.${file.type.split("/")[1]}`, {
        type: file.type,
      });

      const isVideoCreative = String(creativeType || "")
        .toLowerCase()
        .includes("video");

      if (isVideoCreative) {
        if (newFile.type.startsWith("image/")) setThumbnailFile(newFile);
        if (newFile.type.startsWith("video/")) setVideoFile(newFile);
      } else {
        setSelectedFile(newFile);
      }
      e.preventDefault();
      return;
    }
  }, [creativeType]);

  useEffect(() => {
    if (isOpen) document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, [isOpen, handlePaste]);

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: {
      "image/*": [".jpg", ".jpeg", ".png", ".gif"],
      "video/*": [".mp4", ".avi", ".mov", ".wmv"],
    },
    multiple: false,
    disabled: uploading,
  });

  const {
    getRootProps: getThumbnailRootProps,
    getInputProps: getThumbnailInputProps,
    isDragActive: isThumbnailDragActive,
    isDragReject: isThumbnailDragReject,
  } = useDropzone({
    onDrop: onDropThumbnail,
    accept: {
      "image/*": [".jpg", ".jpeg", ".png", ".gif"],
    },
    multiple: false,
    disabled: uploading,
  });

  const {
    getRootProps: getVideoRootProps,
    getInputProps: getVideoInputProps,
    isDragActive: isVideoDragActive,
    isDragReject: isVideoDragReject,
  } = useDropzone({
    onDrop: onDropVideo,
    accept: {
      "video/*": [".mp4", ".avi", ".mov", ".wmv"],
    },
    multiple: false,
    disabled: uploading,
  });

  const handleUpload = async () => {
    const isVideoCreative = String(creativeType || "")
      .toLowerCase()
      .includes("video");

    if (isVideoCreative) {
      if (!thumbnailFile) return Toast("error", "Select a file first");
      if (!videoFile) return Toast("error", "Select a file first");
    } else {
      if (!selectedFile) return Toast("error", "Select a file first");
    }

    setUploading(true);

    try {
      if (isVideoCreative) {
        const thumbnailFormData = new FormData();
        thumbnailFormData.append("files", thumbnailFile);
        thumbnailFormData.append("creative_id", creativeId);
        // Video creatives should produce exactly ONE version/log entry — this
        // thumbnail-only call skips the log write; the video call below writes
        // the single combined entry (see thumbnail_url below).
        thumbnailFormData.append("skip_log", "true");

        const thumbnailRes = await API({
          url: "/upload/alphaImage",
          method: "POST",
          data: thumbnailFormData,
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (thumbnailRes.status !== "SUCCESS") {
          Toast("error", thumbnailRes.data?.message || "Upload failed");
          onUploadError?.(thumbnailRes);
          return;
        }

        const thumbnailUrl = thumbnailRes.data?.data?.url;

        const videoFormData = new FormData();
        videoFormData.append("files", videoFile);
        videoFormData.append("creative_id", creativeId);
        if (thumbnailUrl) videoFormData.append("thumbnail_url", thumbnailUrl);

        const videoRes = await API({
          url: "/upload/alphaVideo",
          method: "POST",
          data: videoFormData,
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (videoRes.status === "SUCCESS") {
          Toast("success", videoRes.message || "Upload successful");
          EmitEvent(creativeId, videoRes.data?.data?.log[0]);
          onUploadSuccess?.(videoRes);
          onClose();
          setThumbnailFile(null);
          setVideoFile(null);
        } else {
          Toast("error", videoRes.data?.message || "Upload failed");
          onUploadError?.(videoRes);
        }
      } else {
        const formData = new FormData();
        formData.append("files", selectedFile);
        formData.append("creative_id", creativeId);

        let url = fileType === "image" ? "/upload/alphaImage" : "/upload/alphaVideo";
        if(creativeType==='promoted_image_story') url = '/upload/alphaPromtedImage';
        const res = await API({
          url,
          method: "POST",
          data: formData,
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (res.status === "SUCCESS") {
          Toast("success", res.message || "Upload successful");
          EmitEvent(creativeId, res.data?.data?.log[0]);
          onUploadSuccess?.(res);
          onClose();
          setSelectedFile(null);
        } else {
          Toast("error", res.data?.message || "Upload failed");
          onUploadError?.(res);
        }
      }
    } catch (err) {
      Toast("error", "Upload failed");
      onUploadError?.(err);
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  const isVideoCreative = String(creativeType || "")
    .toLowerCase()
    .includes("video");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto p-6">
        <div className="flex justify-between mb-4">
          <h3 className="font-semibold">Upload Design</h3>
          <button onClick={onClose} disabled={uploading}>
            ✕
          </button>
        </div>

        {isVideoCreative ? (
          <>
            <div className="font-semibold">Thumbnail</div>
            <div
              {...getThumbnailRootProps()}
              ref={dropzoneRef}
              tabIndex={0}
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer ${isThumbnailDragActive ? "border-blue-400 bg-blue-50" : isThumbnailDragReject ? "border-red-400 bg-red-50" : "border-gray-300 hover:bg-gray-50"}`}
            >
              <input {...getThumbnailInputProps()} />
              {uploading ? <Loader /> : <p>Drag, click, or paste (Ctrl+V)</p>}
            </div>

            {thumbnailPreviewUrl && <RotatableImage src={thumbnailPreviewUrl} alt="thumbnail" className="mt-4 max-h-48 mx-auto rounded" />}

            <div className="font-semibold mt-4">Video</div>
            <div
              {...getVideoRootProps()}
              tabIndex={0}
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer ${isVideoDragActive ? "border-blue-400 bg-blue-50" : isVideoDragReject ? "border-red-400 bg-red-50" : "border-gray-300 hover:bg-gray-50"}`}
            >
              <input {...getVideoInputProps()} />
              {uploading ? <Loader /> : <p>Drag, click, or paste (Ctrl+V)</p>}
            </div>

            {videoPreviewUrl && <video src={videoPreviewUrl} controls className="mt-4 max-h-48 mx-auto rounded" />}
          </>
        ) : (
          <>
            <div
              {...getRootProps()}
              ref={dropzoneRef}
              tabIndex={0}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer ${isDragActive ? "border-blue-400 bg-blue-50" : isDragReject ? "border-red-400 bg-red-50" : "border-gray-300 hover:bg-gray-50"}`}
            >
              <input {...getInputProps()} />
              {uploading ? <Loader /> : <p>Drag, click, or paste (Ctrl+V)</p>}
            </div>

            {previewUrl && fileType === "image" && <RotatableImage src={previewUrl} alt="preview" className="mt-4 max-h-48 mx-auto rounded" />}

            {previewUrl && fileType === "video" && <video src={previewUrl} controls className="mt-4 max-h-48 mx-auto rounded" />}
          </>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} disabled={uploading}>
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={(isVideoCreative ? !thumbnailFile || !videoFile : !selectedFile) || uploading}
            className="bg-blue-600 text-white px-4 py-2 rounded"
          >
            Upload
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadModal;
